/**
 * "You sign in like this, you could also sign in like that" — KAN-346.
 *
 * Shown after a session expires and the person signs in again, to an
 * account that has fewer than two counted sign-in methods. Owner
 * decision 3 of 4: ask every 30 days until the target is met, **always**
 * with a way out.
 *
 * **Where it is mounted is a decision, not an accident** (KAN-375,
 * AUTH_RECOVERY.md §7.1). KAN-346 asked for it in the post-sign-in
 * interstitial and named ``pages/AuthContinue.tsx`` / ``AuthHop.tsx``.
 * Those are the landing pages for a CROSS-HOST session handover — a
 * person who signs in on the host they were already on never passes
 * through them, which is most sessions. So the requirement behind that
 * sentence, *one place for every door*, is met by ``App`` (above the
 * page, both shells) and is NOT met by ``AuthContinue``. A dedicated
 * screen between sign-in and destination was rejected on the other
 * side: every door would have to fire it — password, magic link,
 * passkey, Google, OIDC — which is five writers for one rule.
 *
 * Three things this component deliberately does NOT do:
 *
 * **It does not decide when to appear.** ``auth_methods_prompt_due``
 * arrives from the server, which owns the 30-day window, the stamp and
 * the target (``services/auth_methods.prompt_is_due``). Re-deriving it
 * here would need all three, and three copies of a rule is how the
 * answers start disagreeing — the same reason the count itself is sent
 * rather than summed from the booleans.
 *
 * **It does not name which methods the account already has** beyond what
 * ``/auth/me`` already tells this signed-in person about themselves, and
 * it never says how many passkeys exist on which host: a passkey is
 * bound to its RP ID, so "you have two" and "one of them works here"
 * are different sentences (docs/security/PASSKEYS.md §2). Since KAN-373
 * it renders the *second* sentence — which of the two a line is about
 * changes the wording, never the count.
 *
 * **It cannot collide with the mandatory-2FA screen.** That screen is a
 * sign-in ERROR — the caller holds no session — and this flag only
 * exists inside an authenticated payload. An offer with a *Skip* button
 * next to a wall that says "you must" would tell the user the wall is
 * optional; the two are kept apart by the session, not by ordering.
 */

import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { useAuthMe, useDismissAuthMethodsPrompt } from "@/api/queries";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

export default function AuthMethodsOffer(): JSX.Element | null {
  const { t, i18n } = useTranslation("auth");
  const me = useAuthMe();
  const dismiss = useDismissAuthMethodsPrompt();
  const { pathname } = useLocation();

  const lang = isSupportedLang(i18n.language) ? i18n.language : DEFAULT_LANG;

  // Not signed in, still loading, already covered, or asked recently —
  // all of them mean "nothing to show", and the server has collapsed
  // them into one boolean.
  if (!me.data?.auth_methods_prompt_due) return null;

  // KAN-1981 — never on the shop-floor terminal. It is a shared tablet:
  // the account page the CTA leads to is outside the terminal scope, and
  // so is "Skip" (403). The server already withholds the flag from a
  // terminal session; this covers anyone else standing on ``/shopfloor``.
  if (isShopfloorPath(pathname)) return null;

  // Which of the four the account is missing. Rendered from the flags
  // the payload already carries rather than a second source, so the
  // list cannot claim you lack something you have.
  //
  // Passkey is the one that cannot be answered by a single flag
  // (KAN-373). A credential is bound to its RP ID, so an account with a
  // passkey at another tenant has one it cannot use on this page —
  // dropping the line because ``has_passkey`` is true would be the
  // platform saying "you already have a passkey" to somebody who has
  // nothing here to unlock with, which KAN-346 forbids outright. So the
  // line stays and the WORDING changes: "set one up" versus "you have
  // one elsewhere, add one here".
  const passkeyKey = !me.data.has_passkey
    ? "passkey"
    : !me.data.passkey_usable_here
      ? "passkeyElsewhere"
      : null;
  const missing = [
    !me.data.has_password && "password",
    passkeyKey,
    !me.data.has_google && "google",
  ].filter((value): value is string => typeof value === "string");

  return (
    <div
      role="region"
      aria-label={t("methodsOffer.heading")}
      className="border-b border-border bg-surface-2 px-6 py-4"
      data-testid="auth-methods-offer"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-ink">
            {t("methodsOffer.heading")}
          </p>
          <p className="text-sm text-ink-2">{t("methodsOffer.body")}</p>
          {missing.length > 0 && (
            <ul
              className="mt-1 list-inside list-disc text-sm text-ink-2"
              data-testid="auth-methods-offer-missing"
            >
              {missing.map((key) => (
                <li key={key}>{t(`methodsOffer.methods.${key}`)}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <Link
            to={`/${lang}/account`}
            className="inline-flex items-center justify-center rounded-md bg-ink px-4 py-2 text-sm font-medium text-bg hover:bg-ink"
            data-testid="auth-methods-offer-cta"
          >
            {t("methodsOffer.cta")}
          </Link>
          <button
            type="button"
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
            className="text-sm text-ink-3 underline underline-offset-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="auth-methods-offer-skip"
          >
            {t("methodsOffer.skip")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** ``/<lang>/shopfloor…`` — the terminal, not the admin's shop-floor settings. */
function isShopfloorPath(pathname: string): boolean {
  return /^\/[^/]+\/shopfloor(\/|$)/.test(pathname);
}
